import { useAppState, useDispatch } from 'AppProvider';
import { Divider, Flex } from 'components/common/styled/common-styled';
import { Error, ErrorContainer } from 'components/theme/Form/Form';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { PaymentContext } from 'features/ShoppingCart/contexts/PaymentContext';
import { PaymentIntentError } from 'features/ShoppingCart/types';
import useCartItemsListings from 'hooks/useCartItemsListings';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import React, { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PointEligibleProgram } from '@kouto/types';
import { DiscountCode } from 'types/discount-code';
import { useIsMobile } from 'WindowDimensionProvider';
import ChevronDownIcon from 'assets/chevron-down';
import ChevronRightIcon from 'assets/chevron-right';

import { deleteCreditCode, deleteDiscountCode } from 'actions/discount-code';
import { CreditCodes } from 'types/credit-codes';
import CartSummaryTotal from 'features/ShoppingCart/components/CartSummary/CartSummaryTotal';
import IncludedTaxes from 'features/ShoppingCart/components/CartSummary/IncludedTaxes';
import CartSummaryItem from './CartSummaryItem';
import DiscountForm from './DiscountForm';
import PriceSection from './PriceSection';
import CouponCodeItem from './CouponCodeItem';

import {
  BookSidebar,
  BookSticky,
  CartFooter,
  CartItemsContainer,
  CartStyledList,
  CartStyledListItem,
  CartSummaryTitle,
  CartSummaryToggle,
  CartSummaryWrapperTitle,
  CartTotalSection,
} from './styles';

interface CartSummaryProps {
  isPayingWithPoints?: boolean;
  paymentIntentError?: PaymentIntentError | null;
  findEligible?: boolean;
}

const CartSummary = ({
  isPayingWithPoints,
  paymentIntentError,
  findEligible,
}: CartSummaryProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const { cartItems, listings, isLoading } = useCartItemsListings();
  const {
    cartTotal,
    cartSubTotal,
    totalDiscountAmount,
    cartItemsDetails,
    includedTaxes,
    nonIncludedTaxes,
  } = useContext(PaymentContext);
  const cartItemsValues = Object.values(cartItems);
  const { cartError } = useAppState(
    (state: Record<string, unknown>) => state.booking,
  );

  const isFreeBeforeDiscount = cartTotal === 0 && totalDiscountAmount === 0;

  const hidePricesAndCartTotal =
    cartTotal === 0 &&
    Object.values(listings).every((listing) => listing.hidePrice);

  const {
    discountCode,
    error: validateDiscountCodeError,
    validateDiscountCodeStatus,
  }: DiscountCode = useAppState(
    (state: Record<string, unknown>) => state.discountCode,
  );

  const {
    creditCodes,
    error: validateCreditCodesError,
    validateCreditCodeStatus,
  }: CreditCodes = useAppState(
    (state: Record<string, unknown>) => state.creditCodes,
  );

  const { data: eligibleProgram } = useGetEligibleProgram(
    Object.keys(listings).length > 0
      ? Object.values(listings)[0]?.brandId
      : undefined,
    Object.keys(listings).length > 0
      ? {
          listings: Object.entries(listings)
            .filter(([, listing]) => listing.id)
            .map(([, listing]) => ({
              id: listing.id,
              brandId: listing.brandId,
              productLine: listing.productLine,
            })),
        }
      : null,
  );

  const isHyattEligible = (listingId?: string | null) => {
    if (!listingId) return false;
    if (
      eligibleProgram &&
      eligibleProgram[listingId]?.org === PointEligibleProgram.FIND &&
      eligibleProgram[listingId]?.payEligible
    ) {
      return true;
    }
    return false;
  };

  const removeDiscountCode = () => {
    if (discountCode) {
      dispatch(deleteDiscountCode());
    }
  };

  const removeCreditCode = (code: string) => {
    if (creditCodes) {
      dispatch(deleteCreditCode(code));
    }
  };

  if (!cartItemsValues.length) {
    return (
      <BookSticky>
        <BookSidebar>
          <div>The cart is empty</div>
        </BookSidebar>
      </BookSticky>
    );
  }

  if (isLoading) {
    return (
      <BookSticky>
        <SkeletonLine style={{ height: 300 }} translucent />
      </BookSticky>
    );
  }

  const sortedCartItems = cartItemsValues.sort((exp1, exp2) => {
    return exp1.sessionDateTime.localeCompare(exp2.sessionDateTime);
  });

  return (
    <BookSticky>
      <BookSidebar
        role="region"
        aria-label="reservation"
        className="summary-wrapper cart-summary__book-sidebar"
      >
        <CartSummaryTitle>
          <CartSummaryToggle
            {...(isMobile
              ? {
                  as: 'button' as const,
                  type: 'button',
                  'aria-expanded': isExpanded,
                  onClick: () => setIsExpanded((prev) => !prev),
                }
              : {})}
          >
            <Flex
              direction="row"
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <CartSummaryWrapperTitle className="cart-summary__title">
                {t('reservationSummary')}
              </CartSummaryWrapperTitle>
              <Flex
                direction="row"
                style={{ alignItems: 'center', gap: '8px' }}
              >
                <span>({cartItemsValues.length})</span>
                {isMobile &&
                  (isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />)}
              </Flex>
            </Flex>
          </CartSummaryToggle>
          {cartError && (
            <ErrorContainer style={{ paddingTop: '10px' }}>
              <Error>
                There seem to be an error while booking few or all of the
                experiences.
              </Error>
            </ErrorContainer>
          )}
        </CartSummaryTitle>
        {(!isMobile || isExpanded) && (
          <>
            <CartItemsContainer>
              <CartStyledList aria-label="Cart items list">
                {sortedCartItems.map((cartItem, i) =>
                  cartItemsDetails[cartItem.cartItemId] ? (
                    <Fragment key={cartItem.cartItemId}>
                      <CartStyledListItem>
                        <div role="group" aria-label={`Cart item ${i + 1}`}>
                          <CartSummaryItem
                            paymentIntentError={paymentIntentError}
                            isPayingWithPoints={isPayingWithPoints}
                            cartItem={cartItem}
                            cartItemTotalDetails={
                              cartItemsDetails[cartItem.cartItemId]
                            }
                            listing={
                              listings[
                                cartItem?.listingId || cartItem?.experienceId
                              ]
                            }
                            isHyattEligible={isHyattEligible(
                              listings[
                                cartItem?.listingId || cartItem?.experienceId
                              ].id,
                            )}
                          />
                        </div>
                      </CartStyledListItem>
                      {cartItemsValues.length !== i + 1 && (
                        <Divider space={20} />
                      )}
                    </Fragment>
                  ) : null,
                )}
              </CartStyledList>
            </CartItemsContainer>

            <CartFooter>
              {isFreeBeforeDiscount ? null : (
                <DiscountForm
                  cartExperienceIds={Object.values(cartItems).map(
                    (cartItem) => cartItem.experienceId,
                  )}
                  cartItems={Object.values(cartItems).map((cartItem) => ({
                    experienceId: cartItem.experienceId,
                    sessionDate: cartItem.sessionDateTime?.split('T')[0],
                  }))}
                  findEligible={findEligible}
                  validateDiscountCodeError={validateDiscountCodeError}
                  validateDiscountCodeStatus={validateDiscountCodeStatus}
                  validateCreditCodesError={validateCreditCodesError}
                  validateCreditCodeStatus={validateCreditCodeStatus}
                />
              )}
              {discountCode && (
                <CouponCodeItem
                  discountCode={discountCode}
                  totalCartDiscount={totalDiscountAmount}
                  removeDiscountCode={removeDiscountCode}
                />
              )}
              <PriceSection
                cartTotal={cartTotal}
                cartSubTotal={cartSubTotal}
                creditCodes={creditCodes}
                includedTaxes={includedTaxes}
                isPayingWithPoints={isPayingWithPoints}
                nonIncludedTaxes={nonIncludedTaxes}
                removeCreditCode={removeCreditCode}
              />
            </CartFooter>
          </>
        )}
        {!hidePricesAndCartTotal && (
          <CartTotalSection>
            {(!isMobile || isExpanded) && cartTotal > 0 && (
              <Divider space={0} style={{ marginBottom: '20px' }} />
            )}
            <CartSummaryTotal
              cartTotal={cartTotal}
              isPayingWithPoints={isPayingWithPoints}
            />
            <IncludedTaxes includedTaxes={includedTaxes} />
          </CartTotalSection>
        )}
      </BookSidebar>
    </BookSticky>
  );
};

export default CartSummary;
